import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr'
import decodeJwt from 'jwt-decode'
import { API_ORIGIN } from '../utils/constants/url'
import localStorageIds from '../utils/constants/localStorageIds'

export const REGULATION_PERIOD_MS = 10 * 60 * 1000
export const OVERTIME_PERIOD_MS = 5 * 60 * 1000
export const MAX_PERIOD = 8
export const MAX_SCORE = 999
// Matches the backend's own bound, so a typed clock is never rejected server-side.
export const MAX_CLOCK_MS = (99 * 60 + 59) * 1000

// The operator's browser owns the clock: viewers must see what the operator sees, so the
// value on screen is what goes on the wire. Every action sends at once, and a running clock
// resends about once a second so the broadcast never lags the operator by more than that.
const HEARTBEAT_MS = 1000
const TICK_MS = 100

export const periodLengthMs = (period) =>
  period <= 4 ? REGULATION_PERIOD_MS : OVERTIME_PERIOD_MS

export const periodLabel = (period) => {
  if (period <= 4) return `Q${period}`
  return period === 5 ? 'OT' : `OT${period - 4}`
}

export const formatClock = (ms) => {
  const tenths = Math.ceil(Math.max(0, ms) / 100) / 10
  const minutes = Math.floor(tenths / 60)
  const seconds = tenths - minutes * 60
  // Under a minute a game clock reads in tenths, above it in minutes and seconds.
  return tenths < 60
    ? `${Math.floor(seconds).toString().padStart(2, '0')}.${Math.floor((seconds % 1) * 10)}`
    : `${minutes}:${Math.floor(seconds).toString().padStart(2, '0')}`
}

export const parseClock = (value) => {
  const cleaned = String(value).replace(/[^0-9:.]/g, '').trim()
  if (!cleaned) return null
  if (cleaned.includes(':')) {
    const [minutes, seconds] = cleaned.split(':')
    const ms = (parseInt(minutes || '0', 10) * 60 + parseFloat(seconds || '0')) * 1000
    return Number.isNaN(ms) ? null : ms
  }
  const ms = parseFloat(cleaned) * 1000
  return Number.isNaN(ms) ? null : ms
}

const DEFAULT_BOARD = {
  period: 1,
  home: 0,
  away: 0,
  remainingMs: REGULATION_PERIOD_MS,
  running: false,
}

const clampScore = (value) => Math.max(0, Math.min(MAX_SCORE, value))

/**
 * Drives one game's manual scoreboard over the studio-scoreboard hub.
 *
 * The hub echoes every stored board back to the whole group, this connection included. Our
 * own echoes are ignored by sender id: re-adopting them would restart the local countdown
 * from a value that is already a round trip old, and the clock would lose that much every
 * second. Another operator's update is adopted in full.
 */
const useStudioScoreboard = (orgId, gameId, enabled) => {
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState(null)
  const [board, setBoard] = useState(DEFAULT_BOARD)

  const connectionRef = useRef(null)
  // Wall-clock instant the running clock reaches zero; null whenever it is stopped.
  const endAtRef = useRef(null)
  const latest = useRef(board)
  // False until this connection has delivered the stored board; see the StateUpdated handler.
  const hydrated = useRef(false)
  useEffect(() => {
    latest.current = board
  }, [board])

  const myUserId = useMemo(() => {
    try {
      const token = localStorage.getItem(localStorageIds.TOKEN)
      return token ? decodeJwt(token)?.sub || null : null
    } catch {
      return null
    }
  }, [])

  const push = useCallback(async (next) => {
    const connection = connectionRef.current
    if (!connection) return
    try {
      await connection.invoke('Update', {
        scoreHome: next.home,
        scoreAway: next.away,
        period: next.period,
        clockRemainingMs: Math.round(next.remainingMs),
        clockRunning: next.running,
      })
      setError(null)
    } catch {
      setError('The scoreboard could not be updated.')
    }
  }, [])

  const apply = useCallback(
    (next) => {
      endAtRef.current = next.running ? Date.now() + next.remainingMs : null
      latest.current = next
      setBoard(next)
      push(next)
    },
    [push]
  )

  // Local countdown. Only the display is driven here; what reaches the backend is this same
  // value, sent on the heartbeat below.
  useEffect(() => {
    const id = setInterval(() => {
      if (!endAtRef.current) return
      const remainingMs = Math.max(0, endAtRef.current - Date.now())
      if (remainingMs > 0) {
        setBoard((current) => (current.running ? { ...current, remainingMs } : current))
        return
      }
      endAtRef.current = null
      const stopped = { ...latest.current, remainingMs: 0, running: false }
      latest.current = stopped
      setBoard(stopped)
      push(stopped)
    }, TICK_MS)
    return () => clearInterval(id)
  }, [push])

  useEffect(() => {
    if (!board.running) return undefined
    const id = setInterval(() => push(latest.current), HEARTBEAT_MS)
    return () => clearInterval(id)
  }, [board.running, push])

  useEffect(() => {
    if (!enabled || !orgId || !gameId) return undefined
    const token = localStorage.getItem(localStorageIds.TOKEN)
    if (!token) return undefined

    const connection = new HubConnectionBuilder()
      .withUrl(
        `${API_ORIGIN}/v1/orgs/${orgId}/studio/games/${gameId}/scoreboard/live` +
          `?access_token=${encodeURIComponent(token)}`,
        // The API runs several replicas with no session affinity, and a negotiated connection id
        // lives only in the memory of the replica that issued it — the transport request lands on
        // another one two times in three and is told no such connection exists. The Redis backplane
        // carries messages between replicas, not connection state, so it does not help here.
        // Connecting straight over a websocket skips the handoff: the request that opens the socket
        // is the one that registers it, on whichever replica answers. The token has to travel in the
        // query string, which it already does.
        { skipNegotiation: true, transport: HttpTransportType.WebSockets }
      )
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build()

    connection.on('StateUpdated', (state) => {
      if (!state) return
      // Echoes of our own writes are ignored so the local clock is never yanked about by the
      // round trip — but the first message of a connection is not an echo, it is the stored board
      // the server sends on connect. It usually carries our own id, since we are normally the one
      // who last touched it, so filtering it would throw away exactly the state we reconnected to
      // recover and leave the panel showing a fresh Q1.
      const restoring = !hydrated.current
      hydrated.current = true
      if (!restoring && state.updatedByUserId && state.updatedByUserId === myUserId) return
      const next = {
        period: state.period ?? 1,
        home: clampScore(state.scoreHome ?? 0),
        away: clampScore(state.scoreAway ?? 0),
        remainingMs: state.clockRemainingMs ?? REGULATION_PERIOD_MS,
        running: Boolean(state.clockRunning),
      }
      endAtRef.current = next.running ? Date.now() + next.remainingMs : null
      latest.current = next
      setBoard(next)
    })
    connection.onreconnected(() => {
      // The hub sends the stored board again on every connect, including this one.
      hydrated.current = false
      setConnected(true)
    })
    connection.onclose(() => setConnected(false))

    connectionRef.current = connection
    hydrated.current = false
    connection
      .start()
      .then(() => {
        setConnected(true)
        setError(null)
      })
      .catch(() => setError('Could not connect to the scoreboard.'))

    return () => {
      connectionRef.current = null
      connection.stop()
    }
  }, [enabled, orgId, gameId, myUserId])

  const addPoints = useCallback(
    (team, points) => {
      const current = latest.current
      apply({ ...current, [team]: clampScore(current[team] + points) })
    },
    [apply]
  )

  const setScore = useCallback(
    (team, value) => {
      const current = latest.current
      apply({ ...current, [team]: clampScore(parseInt(value, 10) || 0) })
    },
    [apply]
  )

  const stepPeriod = useCallback(
    (delta) => {
      const current = latest.current
      const period = Math.min(MAX_PERIOD, Math.max(1, current.period + delta))
      if (period === current.period) return
      // A new period starts on a full, stopped clock.
      apply({ ...current, period, running: false, remainingMs: periodLengthMs(period) })
    },
    [apply]
  )

  const setClockMs = useCallback(
    (ms) => {
      const current = latest.current
      apply({
        ...current,
        running: false,
        remainingMs: Math.max(0, Math.min(MAX_CLOCK_MS, ms)),
      })
    },
    [apply]
  )

  const toggleClock = useCallback(() => {
    const current = latest.current
    if (current.running) {
      apply({ ...current, running: false })
      return
    }
    if (current.remainingMs <= 0) return
    apply({ ...current, running: true })
  }, [apply])

  const resetClock = useCallback(() => {
    const current = latest.current
    apply({ ...current, running: false, remainingMs: periodLengthMs(current.period) })
  }, [apply])

  return {
    connected,
    error,
    board,
    addPoints,
    setScore,
    stepPeriod,
    setClockMs,
    toggleClock,
    resetClock,
  }
}

export default useStudioScoreboard
